import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants/http-header.constants';
import { AggregateResponse } from '@r1-compass-library/models/reporting/aggregate-response.model';
import { PlayResult } from '@r1-compass-library/models/reporting/reporting-data.model';
import { ReportingAggregateRequest } from '@r1-compass-library/models/reporting/reporting-request.model';
import { PlayResultSearch } from '@r1-compass-library/models/reporting/reporting-search.model';
import { AllCharacterHttpParamsEncoder } from '@r1-compass-library/utilities/AllCharacterHttpParamsEncoder';
import { Observable, catchError, finalize, of, retry } from 'rxjs';
import { PlayReportingService } from './play-reporting.service';
import { Environment } from '@r1-compass-library/environments/environment';
import { CountResponse } from '@r1-compass-library/models/search/count-response.model';
import { SpinnerService } from '@r1-compass-library/services/spinner.service';

@Injectable({
    providedIn: 'root',
})
export class PlayResultService extends PlayReportingService {
    readonly #http = inject(HttpClient);
    protected readonly spinnerService: SpinnerService = inject(SpinnerService);

    private RESULTS_URL = Environment.baseUrl + '/frontend/play-results';

    constructor() {
        super();
    }

    listPlayResults(playId: number, rangeStart: string, rangeEnd: string): Observable<PlayResult[]> {
        const processName = 'PlayResultService.listPlayResults';
        this.spinnerService.addProcess(processName, 'Loading play-results...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
                fromObject: {
                    playId: playId,
                    executionStartRangeBegin: rangeStart,
                    executionStartRangeEnd: rangeEnd,
                },
            }),
        };

        return this.#http.get<PlayResult[]>(this.RESULTS_URL, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<PlayResult[]>();
            }),
            finalize(() => this.spinnerService.completeProcess(processName))
        );
    }

    getCounts(search: PlayResultSearch, groupBy: string): Observable<CountResponse<string>[]> {
        const processName = 'PlayResultService.getCounts';
        this.spinnerService.addProcess(processName, 'Getting reporting counts...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
            }),
        };

        this.addSearchParams(search, customHttpOptions);
        customHttpOptions.params = customHttpOptions.params.set('groupBy', groupBy);

        return this.#http.get<CountResponse<string>[]>(`${this.RESULTS_URL}/counts`, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<CountResponse<string>[]>();
            }),
            finalize(() => this.spinnerService.completeProcess(processName))
        );
    }

    getAggregates(request: ReportingAggregateRequest): Observable<AggregateResponse[]> {
        const processName = 'PlayResultService.getAggregates';
        this.spinnerService.addProcess(processName, 'Getting reporting aggregates...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
                fromObject: {
                    operation: request.operation,
                    field: request.field,
                },
            }),
        };

        this.addSearchParams(request.search, customHttpOptions);

        if (request.groupBy) {
            customHttpOptions.params = customHttpOptions.params.append('groupBy', request.groupBy);
        }

        if (request.groupByJoinAttribute) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupByJoinAttribute',
                request.groupByJoinAttribute
            );
        }

        if (request.groupBySecondsRange) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupBySecondsRange',
                request.groupBySecondsRange.toString()
            );
        }

        if (request.groupBySecondsOffset) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupBySecondsOffset',
                request.groupBySecondsOffset.toString()
            );
        }

        customHttpOptions.params = customHttpOptions.params.delete('search').delete('resultsUrl');

        return this.#http.get<AggregateResponse[]>(`${this.RESULTS_URL}/aggregates`, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<AggregateResponse[]>();
            }),
            finalize(() => this.spinnerService.completeProcess(processName))
        );
    }

    getJoinedAggregates(search: PlayResultSearch): Observable<{ [key: string]: any }[]> {
        const processName = 'PlayResultService.getJoinedAggregates';
        this.spinnerService.addProcess(processName, 'Getting joined aggregates...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
            }),
        };

        this.addSearchParams(search, customHttpOptions);

        return this.#http
            .get<{ [key: string]: any }[]>(`${this.RESULTS_URL}/joined-aggregates`, customHttpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of<{ [key: string]: any }[]>();
                }),
                finalize(() => this.spinnerService.completeProcess(processName))
            );
    }
}
